import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import "./Fonts/Lato/Lato-Regular.ttf";
import ChunkLoadErrorBoundary from "./components/Errors/ChunkLoadErrorBoundary";
import { DistrictContextWrapper } from "./screens/JaldiPortals/JudicialVacancy/DistrictContextWrapper";
import { SsrContent } from "./ssr/SsrContent";
import { seedSsrApiCache } from "./ssr/ssrHydrationCache";
import { persistor } from "./redux/store/store";
import {
  replaceSsrWithClientApp,
  runAfterPersistenceRehydration,
} from "./ssr/hydrationBootstrap";

const app = (
  <ChunkLoadErrorBoundary>
    <DistrictContextWrapper>
      <App />
    </DistrictContextWrapper>
  </ChunkLoadErrorBoundary>
);

const rootElement = document.getElementById("root");

// Two different SSR systems can have produced this page (see
// server/page-ssr.js and server/serverRender.js) - each embeds its payload
// under a different script id, so which one is present says which
// happened, and each needs a different client bootstrap:
//
// - __JALDI_SSR_API_CACHE__ (server/page-ssr.js's REAL_SSR_ROUTES /
//   server/seo.js's COURT_INFRASTRUCTURE_REPORT_PATH, via
//   src/ssr/serverRender.js): the server rendered the REAL `app` tree
//   below with real screen components - so the client must hydrate onto
//   that SAME tree, seeding the identical endpoint->body cache first so
//   every screen's useState(() => peekSsrApiResultSanitized(...)) lazy
//   initializer sees the exact data the server saw for its own first
//   render (both the server's renderToString pass and the client's first,
//   pre-hydration hydrateRoot pass need to agree, or React logs a
//   hydration mismatch and patches the DOM to match the client's version -
//   silently discarding some of the server-rendered content it exists to
//   deliver).
// - __JALDI_SSR_DATA__ (every other route, still the generic placeholder
//   system): unchanged from before - hydrates the placeholder SsrContent
//   markup, then swaps to the real `app` tree via a normal (non-hydrating)
//   client render once mounted, exactly as it always has.
const apiCacheElement = document.getElementById("__JALDI_SSR_API_CACHE__");
let realSsrApiCache = null;
try {
  realSsrApiCache = apiCacheElement ? JSON.parse(apiCacheElement.textContent) : null;
} catch (_error) {
  realSsrApiCache = null;
}

const payloadElement = document.getElementById("__JALDI_SSR_DATA__");
let initialSsrModel = null;
try {
  initialSsrModel = payloadElement ? JSON.parse(payloadElement.textContent) : null;
} catch (_error) {
  initialSsrModel = null;
}

const ClientBootstrap = ({ initialModel }) => {
  const [interactive, setInteractive] = React.useState(!initialModel);
  React.useEffect(() => setInteractive(true), []);
  return interactive ? app : <SsrContent model={initialModel} />;
};

if (realSsrApiCache) {
  seedSsrApiCache(realSsrApiCache);
  // The SSR renderer and browser app intentionally use different router/store
  // shells (StaticRouter + an SSR store versus RouterProvider + PersistGate),
  // so they are not a safe hydration pair. Hydration can leave the server tree
  // in place and append the client tree, repeating the whole page. Keep the SSR
  // HTML visible while persistence loads, then atomically hand #root to one
  // normal client root. The original SSR HTML is still what crawlers and
  // no-JavaScript clients receive.
  runAfterPersistenceRehydration(persistor, () => {
    replaceSsrWithClientApp(rootElement, ReactDOM.createRoot, app);
  });
} else if (initialSsrModel) {
  ReactDOM.hydrateRoot(rootElement, <ClientBootstrap initialModel={initialSsrModel} />);
} else {
  ReactDOM.createRoot(rootElement).render(app);
}
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
